import { useCategories } from "@/api/cms";
import { MediaPickerField } from "@/components/cms-puck/fields/MediaPickerField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { getMainLanguage, constructLocaleDict } from "@/i18n";
import type {
  CatalogueRaisonneSection,
  CatalogueRaisonneSectionUpsert,
} from "@/lib/cms/cms-types";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const DECOUPAGE_TYPES_ARTWORKS = [
  "creationDate",
  "categories",
  "series",
  "themes",
  "registrationNumber",
  "type",
] as const;

export const DECOUPAGE_TYPES_MULTIPRINTS = [
  "creationDate",
  "registrationNumber",
  "type",
] as const;

export const DECOUPAGE_SORT_OPTIONS = [
  "dateCreation:asc",
  "dateCreation:desc",
  "title:asc",
  "title:desc",
  "numeroEnregistrement:asc",
  "numeroEnregistrement:desc",
  "createdAt:asc",
  "createdAt:desc",
] as const;

const CATEGORY_TYPES = ["categories", "series", "themes", "type"] as const;

type Target = "ARTWORKS" | "MULTIPRINTS";

function sectionToUpsert(
  section: CatalogueRaisonneSection | CatalogueRaisonneSectionUpsert,
): CatalogueRaisonneSectionUpsert {
  return {
    id: section.id,
    nameMultilingual: section.nameMultilingual ?? {
      fr: "",
      en: "",
      es: "",
      nl: "",
    },
    titleMultilingual: section.titleMultilingual ?? {
      fr: "",
      en: "",
      es: "",
      nl: "",
    },
    contentMultilingual: section.contentMultilingual ?? {
      fr: "",
      en: "",
      es: "",
      nl: "",
    },
    showContent: section.showContent ?? false,
    startValue: section.startValue ?? "",
    endValue: section.endValue ?? "",
    categoryId: section.categoryId ?? "",
    categoryMultilingual:
      section.categoryMultilingual ?? constructLocaleDict(() => ""),
    vignette: section.vignette ?? null,
    order: section.order ?? 0,
    type: section.type || "creationDate",
    sortByInsideDecoupage: section.sortByInsideDecoupage ?? "dateCreation:asc",
  };
}

/**
 * Éditeur compact d'UN découpage CR.
 * Type + tri = niveau bloc (commun), comme l'ancien CMS — pas ici.
 * Les champs (catégorie / plage) changent selon `decoupageType`.
 */
export function DecoupageSectionInlineEditor({
  target,
  section,
  decoupageType,
  sortByInsideDecoupage,
  onSave,
  saving,
}: {
  target: Target;
  section: CatalogueRaisonneSection | CatalogueRaisonneSectionUpsert;
  /** Type partagé (défini dans les réglages communs du bloc). */
  decoupageType: string;
  /** Tri partagé (défini dans les réglages communs du bloc). */
  sortByInsideDecoupage: string;
  onSave: (next: CatalogueRaisonneSectionUpsert) => void;
  saving?: boolean;
}) {
  const cmsNew = useCmsNew();
  const { t } = useTranslation();
  const mainLang = getMainLanguage();
  // Champs pilotés par le type du découpage édité (valeurs en base), pas seulement
  // le select commun — évite d'afficher « Année début » vide sur un thèmes/catégorie.
  const effectiveType = section.type || decoupageType;
  const [draft, setDraft] = useState(() =>
    sectionToUpsert({
      ...section,
      type: effectiveType,
      sortByInsideDecoupage,
    }),
  );

  useEffect(() => {
    setDraft(
      sectionToUpsert({
        ...section,
        type: section.type || decoupageType,
        sortByInsideDecoupage,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync valeurs section
  }, [
    section.id,
    section.startValue,
    section.endValue,
    section.categoryId,
    section.type,
    decoupageType,
    sortByInsideDecoupage,
  ]);

  useEffect(() => {
    const sectionId = String(section.id ?? "");
    const onVignette = (ev: Event) => {
      const detail = (ev as CustomEvent<{ sectionId?: string; mediaId?: string }>)
        .detail;
      if (!detail?.mediaId || detail.sectionId !== sectionId) return;
      setDraft((prev) => ({
        ...prev,
        vignette: { id: detail.mediaId } as CatalogueRaisonneSectionUpsert["vignette"],
      }));
    };
    window.addEventListener("cms-puck-dec-section-vignette", onVignette);
    return () =>
      window.removeEventListener("cms-puck-dec-section-vignette", onVignette);
  }, [section.id]);

  const fieldType = draft.type || effectiveType || decoupageType;
  const needsCategory = (CATEGORY_TYPES as readonly string[]).includes(
    fieldType,
  );
  const needsRange = ["creationDate", "registrationNumber"].includes(fieldType);
  const isYearRange = fieldType === "creationDate";

  // Parité ancien CMS : useCategories(selectedType) — y compris "type".
  const { data: categories, isLoading: isLoadingCategories } = useCategories(
    needsCategory ? fieldType : undefined,
  );

  const categoryOptions = Array.isArray(categories) ? categories : [];

  const categoryLabel =
    target === "MULTIPRINTS" && fieldType === "type"
      ? cmsNew(
          "cms.new.auto.label.type_estampe_section",
          "Type d'estampe / multiple",
        )
      : cmsNew("cms.new.auto.label.filtre", "Filtre");

  const categoryPlaceholder =
    target === "MULTIPRINTS" && fieldType === "type"
      ? cmsNew(
          "cms.new.auto.placeholder.select_type_multiprint",
          "Sélectionner un type d'estampe / multiple",
        )
      : cmsNew("cms.new.auto.placeholder.choisir", "Choisir…");

  const patch = (partial: Partial<CatalogueRaisonneSectionUpsert>) =>
    setDraft((prev) => ({ ...prev, ...partial }));

  const nameValue =
    (draft.nameMultilingual as Record<string, string>)?.[mainLang] ?? "";

  return (
    <div className="flex flex-col gap-3 rounded border border-slate-200 bg-white p-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
        {cmsNew(
          "cms.new.auto.label.edition_decoupage_cr",
          "Découpage (catalogue raisonné)",
        )}
      </p>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-slate-600">
          {cmsNew("cms.new.auto.label.nom", "Nom")} ({mainLang})
        </Label>
        <Input
          value={nameValue}
          onChange={(e) =>
            patch({
              nameMultilingual: {
                ...draft.nameMultilingual,
                [mainLang]: e.target.value,
              } as CatalogueRaisonneSectionUpsert["nameMultilingual"],
            })
          }
        />
      </div>

      {needsRange ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-600">
              {isYearRange ? t("startYear") : t("startNumber")}
            </Label>
            <Input
              value={draft.startValue ?? ""}
              onChange={(e) => patch({ startValue: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-600">
              {isYearRange ? t("endYear") : t("endNumber")}
            </Label>
            <Input
              value={draft.endValue ?? ""}
              onChange={(e) => patch({ endValue: e.target.value })}
            />
          </div>
        </div>
      ) : null}

      {needsCategory ? (
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-600">{categoryLabel}</Label>
          <Select
            value={draft.categoryId || undefined}
            onValueChange={(v) => patch({ categoryId: v })}
            disabled={isLoadingCategories}
          >
            <SelectTrigger className="h-8 bg-white">
              <SelectValue placeholder={categoryPlaceholder} />
            </SelectTrigger>
            <SelectContent className="z-[110]">
              {categoryOptions.map((c: { value?: string; label?: string }) => {
                const value = c.value ?? c.label ?? "";
                if (!value) return null;
                return (
                  <SelectItem key={value} value={value}>
                    {c.label ?? c.value}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <MediaPickerField
        fieldName={`__decSectionVignette__:${draft.id ?? section.id ?? "new"}`}
        value={
          typeof draft.vignette === "object" && draft.vignette
            ? (draft.vignette as { id?: string }).id
            : undefined
        }
        onChange={(attachmentId) =>
          patch({
            vignette: attachmentId
              ? ({
                  id: attachmentId,
                } as CatalogueRaisonneSectionUpsert["vignette"])
              : null,
          })
        }
        label={cmsNew("cms.new.auto.label.vignette", "Vignette")}
        allowedCatalogKinds={["image"]}
      />

      <label className="flex items-center justify-between gap-2 text-xs text-slate-600">
        {cmsNew(
          "cms.new.auto.label.afficher_contenu",
          "Afficher titre / contenu",
        )}
        <Switch
          checked={!!draft.showContent}
          onCheckedChange={(showContent) => patch({ showContent })}
        />
      </label>

      <Button
        type="button"
        size="sm"
        disabled={saving}
        onClick={() =>
          onSave({
            ...draft,
            // Conserver le type de la section (ou celui du select pour une création).
            type: draft.type || fieldType || decoupageType,
            sortByInsideDecoupage,
          })
        }
      >
        {saving
          ? cmsNew("cms.new.auto.text.enregistrement", "Enregistrement…")
          : cmsNew(
              "cms.new.auto.action.save_decoupage",
              "Enregistrer le découpage",
            )}
      </Button>
    </div>
  );
}

export function createEmptyDecoupageUpsert(
  order: number,
  type = "creationDate",
  sortByInsideDecoupage = "dateCreation:asc",
): CatalogueRaisonneSectionUpsert {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `sec-${Date.now()}`;
  return {
    id,
    nameMultilingual: { fr: "Nouveau découpage", en: "", es: "", nl: "" },
    titleMultilingual: { fr: "", en: "", es: "", nl: "" },
    contentMultilingual: { fr: "", en: "", es: "", nl: "" },
    showContent: false,
    categoryMultilingual: constructLocaleDict(() => ""),
    startValue: "",
    endValue: "",
    categoryId: "",
    order,
    vignette: null,
    type,
    sortByInsideDecoupage,
  };
}
